import { useMediaAssetObjectUrl } from '../../canned-responses/use-media-asset-object-url';

interface MediaMessageContentProps {
  kind: 'image' | 'video' | 'audio';
  mediaAssetId: string;
  caption: string;
}

/**
 * MediaMessageContent — bolha de mídia outbound (Respostas Prontas do
 * Operador S2, Task 7).
 *
 * Espelha o padrão de `MessageAttachment` (Item 36 PR-B2): busca o conteúdo
 * autenticado via `useMediaAssetObjectUrl` (o `mediaAssetContentUrl` cru dá
 * 401 num `<img src>` puro — sem cookie de sessão, o browser não injeta o
 * `Authorization: Bearer`). `isLoading` → skeleton neutro (reusa o keyframe
 * global `lume-skeleton-pulse` já usado no Sidebar); `isError` → card
 * honesto "Mídia indisponível" (nunca falha silenciosamente). A legenda
 * (`caption` = `message.content`) é conteúdo REAL da mensagem — sempre
 * renderizada abaixo da mídia como texto normal, em qualquer estado.
 */
export function MediaMessageContent({
  kind,
  mediaAssetId,
  caption,
}: MediaMessageContentProps): JSX.Element {
  const { url, isLoading, isError } = useMediaAssetObjectUrl(mediaAssetId);

  const captionEl =
    caption.length > 0 ? (
      <span
        className="whitespace-pre-wrap break-words"
        style={{ display: 'block', marginTop: 4, fontSize: 13 }}
      >
        {caption}
      </span>
    ) : null;

  if (isLoading) {
    return (
      <div>
        <div
          role="status"
          aria-label="Carregando mídia…"
          style={{
            width: 220,
            height: 160,
            maxWidth: 280,
            background: 'var(--bg-surface-2)',
            borderRadius: 8,
            animation: 'lume-skeleton-pulse 1.6s ease-in-out infinite',
          }}
        />
        {captionEl}
      </div>
    );
  }

  if (isError || url === null) {
    return (
      <div>
        <div
          style={{
            padding: 8,
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          Mídia indisponível
        </div>
        {captionEl}
      </div>
    );
  }

  return (
    <div>
      {kind === 'image' && (
        <a href={url} target="_blank" rel="noreferrer" aria-label="Abrir mídia em nova aba">
          <img
            src={url}
            alt={caption}
            style={{
              maxWidth: 280,
              maxHeight: 280,
              borderRadius: 8,
              cursor: 'pointer',
              display: 'block',
            }}
          />
        </a>
      )}
      {kind === 'video' && (
        <video controls src={url} style={{ maxWidth: 280, borderRadius: 8, display: 'block' }} />
      )}
      {kind === 'audio' && <audio controls src={url} style={{ display: 'block', width: 260 }} />}
      {captionEl}
    </div>
  );
}
